import { useState, useCallback } from 'react';
import { transactionService } from '../services/transactionService';
import { shiftEnhancedService } from '../services/shiftEnhancedService';

const useReports = (locationId) => {
  const [stats, setStats] = useState({
    receiptsToday: 0,
    dailyRevenue: 0,
    averageReceipt: 0,
  });
  const [salesTarget, setSalesTarget] = useState({
    target_amount: 0,
    current_revenue: 0,
    remaining_amount: 0,
    progress_percentage: 0,
    has_target: false
  });
  const [dailyClosureReports, setDailyClosureReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showClosureDetailsModal, setShowClosureDetailsModal] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
    cashier: ''
  });

  const loadDailyStats = useCallback(async () => {
    if (!locationId) return;

    try {
      const response = await transactionService.getDailyStats(locationId);
      if (response.success) {
        setStats({
          receiptsToday: parseInt(response.data.today_transactions) || 0,
          dailyRevenue: parseFloat(response.data.today_revenue) || 0,
          averageReceipt: parseFloat(response.data.today_average_transaction) || 0,
        });
      }
    } catch (err) {
      console.error("Błąd ładowania statystyk:", err);
    }
  }, [locationId]);

  const loadDailyClosureReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const filters = { ...reportFilters };
      if (locationId) filters.location_id = locationId;
      const response = await shiftEnhancedService.getDailyClosureReports(filters);
      if (response.success) {
        setDailyClosureReports(response.data?.reports || response.data || []);
      } else {
        setDailyClosureReports([]);
      }
    } catch (err) {
      console.error("Błąd ładowania raportów:", err);
      setDailyClosureReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, [reportFilters, locationId]);

  const loadReportDetails = useCallback(async (reportId) => {
    try {
      const response = await shiftEnhancedService.getDailyClosureReportDetails(reportId);
      if (response.success) {
        setSelectedReport(response.data);
      }
    } catch (err) {
      console.error("Błąd ładowania szczegółów raportu:", err);
    }
  }, []);

  const loadSalesTarget = useCallback(async () => {
    if (!locationId) return;

    try {
      const response = await transactionService.getSalesTarget(locationId);
      if (response.success && response.data) {
        const targetData = {
          target_amount: response.data.target?.target_amount || 0,
          current_revenue: response.data.current_revenue || 0,
          remaining_amount: response.data.remaining || 0,
          progress_percentage: response.data.progress_percentage || 0,
          has_target: !!response.data.target
        };

        setSalesTarget(targetData);
      } else {
        setSalesTarget({
          target_amount: 0,
          current_revenue: 0,
          remaining_amount: 0,
          progress_percentage: 0,
          has_target: false
        });
      }
    } catch (err) {
      console.error("Błąd ładowania celu sprzedaży:", err);
      setSalesTarget({
        target_amount: 0,
        current_revenue: 0,
        remaining_amount: 0,
        progress_percentage: 0,
        has_target: false
      });
    }
  }, [locationId]);

  return {
    stats,
    setStats,
    salesTarget,
    setSalesTarget,
    dailyClosureReports,
    setDailyClosureReports,
    reportsLoading,
    selectedReport,
    setSelectedReport,
    showClosureDetailsModal,
    setShowClosureDetailsModal,
    reportFilters,
    setReportFilters,
    loadDailyStats,
    loadDailyClosureReports,
    loadReportDetails,
    loadSalesTarget
  };
};

export default useReports;
